import { parse, stringify, TomlError } from 'smol-toml';

export type OnFail = 'spam' | 'trash' | 'pass' | 'reject';

export interface FormField {
  name: string;
  field: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  multiple?: boolean;
  is_min?: number;
  is_max?: number;
  is_in?: string;
  is_pattern?: string;
  is_email?: boolean;
  is_url?: boolean;
  is_empty?: boolean;
  is_not_empty?: boolean;
  helptext?: string;
  on_fail?: OnFail;
  map_to?: string;
  hidden?: boolean;
  discard?: boolean;
  options?: string;
  /** Deprecated. The API parses it and ignores it: spam scoring covers the
   * whole submission, not a nominated field. Kept on the type so specs that
   * carry it round-trip through the editor unchanged. */
  check_spam?: boolean;
  value?: any;
}

export interface FormSettings {
  discard_additional_fields?: boolean;
}

export interface FormSpec {
  fields: { [key: string]: FormField };
  settings: FormSettings;
}

// Mirrors backend/formshive/src/api/core_digest/spec.rs.
export const VALID_FIELD_TYPES = [
  'email',
  'text',
  'textarea',
  'select',
  'checkbox',
  'radio',
  'hidden',
  'submit',
  'number',
  'date',
  'file',
  'tel',
  'url',
  'datetime',
  'time',
] as const;
export const LENGTH_CONSTRAINED_FIELDS = ['text', 'textarea'];
export const VALUE_CONSTRAINED_FIELDS = ['number'];
export const TEXT_ONLY_FIELDS = ['text', 'textarea', 'email', 'url', 'tel'];
const EMAIL_FIELD_TYPES = ['email', 'text'];
const URL_FIELD_TYPES = ['url', 'text'];
export const ON_FAIL_VALUES: OnFail[] = ['reject', 'spam', 'trash', 'pass'];

const NAME_PATTERN = /^[\p{L}\p{N}_-]+$/u;

export const isValidFieldName = (name: string) => NAME_PATTERN.test(name);

/** Same rule as the backend: JS syntax check plus the lookaround the Rust engine lacks. */
export function regexError(pattern: string): string | null {
  if (/\(\?<?[=!]/.test(pattern)) {
    return 'Lookahead and lookbehind assertions are not supported.';
  }
  try {
    new RegExp(pattern);
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : 'Invalid regular expression.';
  }
}

/** A text field hidden from people that must stay empty: only bots fill it in. */
export const isTrapField = (field: FormField) =>
  field.field === 'text' && field.hidden === true && field.is_empty === true;

export const makeTrapField = (name: string): FormField => ({
  name,
  field: 'text',
  hidden: true,
  is_empty: true,
  on_fail: 'spam',
});

export function validatFormSpec(formSpec: FormSpec): {
  isValid: boolean;
  errors: Map<string, string>;
} {
  const errors = new Map<string, string>();

  Object.entries(formSpec.fields).forEach(([key, field]) => {
    if (!isValidFieldName(key)) {
      errors.set(key, 'Field key must contain only letters, numbers, hyphens and underscores.');
    }
    if (!field.name) {
      errors.set(`${key}.name`, 'Field name is missing.');
    } else if (!isValidFieldName(field.name)) {
      errors.set(
        `${key}.name`,
        'Field name must contain only letters, numbers, hyphens and underscores.'
      );
    }
    if (!field.field) {
      errors.set(`${key}.field`, 'Field type is missing.');
      return;
    }
    if (!(VALID_FIELD_TYPES as readonly string[]).includes(field.field)) {
      errors.set(`${key}.field`, `Unknown field type '${field.field}'.`);
      return;
    }

    const type = field.field;
    const lengthOrValue =
      LENGTH_CONSTRAINED_FIELDS.includes(type) || VALUE_CONSTRAINED_FIELDS.includes(type);
    if (field.is_min !== undefined && !lengthOrValue) {
      errors.set(`${key}.is_min`, `is_min is not applicable for field type '${type}'.`);
    }
    if (field.is_max !== undefined && !lengthOrValue) {
      errors.set(`${key}.is_max`, `is_max is not applicable for field type '${type}'.`);
    }
    if (lengthOrValue && field.is_min !== undefined && field.is_max !== undefined) {
      if (field.is_min < 0) errors.set(`${key}.is_min`, 'is_min cannot be negative.');
      if (field.is_max < 0) errors.set(`${key}.is_max`, 'is_max cannot be negative.');
      if (field.is_min > field.is_max) {
        errors.set(`${key}.range`, 'is_min cannot be greater than is_max.');
      }
    }

    if (field.is_in !== undefined && field.is_in.trim() === '') {
      errors.set(`${key}.is_in`, 'is_in must contain a comma-separated list.');
    }

    if (field.is_pattern !== undefined) {
      if (!TEXT_ONLY_FIELDS.includes(type)) {
        errors.set(`${key}.is_pattern`, `is_pattern is not applicable for field type '${type}'.`);
      } else {
        const err = regexError(field.is_pattern);
        if (err) errors.set(`${key}.is_pattern`, `is_pattern is not a valid pattern: ${err}`);
      }
    }
    if (field.is_email && !EMAIL_FIELD_TYPES.includes(type)) {
      errors.set(`${key}.is_email`, `is_email is not applicable for field type '${type}'.`);
    }
    if (field.is_url && !URL_FIELD_TYPES.includes(type)) {
      errors.set(`${key}.is_url`, `is_url is not applicable for field type '${type}'.`);
    }
    if (field.is_empty && field.is_not_empty) {
      errors.set(`${key}.constraints`, 'A field cannot be both is_empty and is_not_empty.');
    }

    if (field.on_fail !== undefined && !ON_FAIL_VALUES.includes(field.on_fail)) {
      errors.set(`${key}.on_fail`, 'on_fail must be one of pass, spam, trash, reject.');
    }
  });

  if (formSpec.settings?.discard_additional_fields === undefined) {
    errors.set(
      'settings.discard_additional_fields',
      'discard_additional_fields setting is missing.'
    );
  }

  return {
    isValid: errors.size === 0,
    errors,
  };
}

export type FormSpecParseResult =
  | { ok: true; spec: FormSpec }
  | { ok: false; error: string; line?: number; column?: number };

const isTable = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);

/** Parse for an editor: reports where the TOML breaks instead of returning undefined. Empty text is a spec with no fields. */
export function parseFormSpecToml(text: string): FormSpecParseResult {
  if (text.trim() === '') {
    return { ok: true, spec: { fields: {}, settings: { discard_additional_fields: false } } };
  }
  let parsed: Record<string, unknown>;
  try {
    parsed = parse(text) as Record<string, unknown>;
  } catch (e) {
    if (e instanceof TomlError) {
      const message = e.message.split('\n')[0].replace(/^Invalid TOML document:\s*/, '');
      return { ok: false, error: message, line: e.line, column: e.column };
    }
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }

  const fields: FormSpec['fields'] = {};
  for (const [key, value] of Object.entries(parsed)) {
    if (key === 'settings') continue;
    if (!isTable(value)) {
      return {
        ok: false,
        error: `"${key}" must be a table: write it as [${key}] on its own line.`,
      };
    }
    fields[key] = value as unknown as FormField;
  }
  const settings = isTable(parsed.settings) ? (parsed.settings as FormSettings) : {};
  return { ok: true, spec: { fields, settings } };
}

export function parseTomlToFormSpec(tomlString?: string): FormSpec | undefined {
  if (!tomlString || tomlString.trim() === '') {
    return undefined;
  }
  const result = parseFormSpecToml(tomlString);
  if (!result.ok) {
    console.error('Failed to parse TOML:', result.error);
    return undefined;
  }
  return result.spec;
}

export function dumpFormSpecToToml(formSpec: FormSpec): string {
  return stringify({
    ...formSpec.fields,
    settings: formSpec.settings ?? { discard_additional_fields: false },
  });
}

export function parseAndValidateFormSpec(tomlString?: string): {
  formSpec: FormSpec | null;
  isValid: boolean;
  errors: Map<string, string>;
} {
  if (!tomlString || tomlString.trim() === '') {
    return {
      formSpec: null,
      // Empty = no form spec
      isValid: true,
      errors: new Map(),
    };
  }
  const result = parseFormSpecToml(tomlString);
  if (!result.ok) {
    return {
      formSpec: null,
      isValid: false,
      errors: new Map([['Error', `Failed to parse TOML. ${result.error}`]]),
    };
  }
  return {
    formSpec: result.spec,
    ...validatFormSpec(result.spec),
  };
}
